// Download Manager — MasterAdmin back-office tool to export bookings for past
// date ranges. The Doctor filter, date range and "Download bookings" export are
// wired to POST /api/v2/booking-export (one call per doctor; rows merged
// client-side into a single csv/xlsx). The TAT card below is its own export.

import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import * as XLSX from "xlsx";
import { MdDownload } from "react-icons/md";
import Header from "../components/organisms/Header";
import BookingsExportCard from "../components/organisms/BookingsExportCard";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import { getDoctorListByHospitalId } from "../lib/apis/doctor";
import { exportBookings, BookingExportRow } from "../lib/apis/reports";

type Format = "csv" | "xlsx";

interface DoctorOption {
  id: string; // mapping_id
  label: string;
}

// Multi-select doctor filter. Empty selection ⇒ "All doctors".
const DoctorMultiSelect: React.FC<{
  doctors: DoctorOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ doctors, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const allSelected = selected.length === 0;

  const toggle = (id: string) =>
    onChange(
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id]
    );

  return (
    <div ref={ref} className="relative">
      {/* Trigger is a div (not a button) so the chip × controls can nest. */}
      <div
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[38px] px-2 py-1.5 text-sm border border-gray-300 rounded bg-white flex items-center justify-between gap-2 cursor-pointer"
      >
        {allSelected ? (
          <span className="px-1 text-gray-700">All doctors</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {selected.map((id) => {
              const d = doctors.find((x) => x.id === id);
              if (!d) return null;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700"
                >
                  {d.label}
                  <span
                    role="button"
                    aria-label={`Remove ${d.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(id);
                    }}
                    className="cursor-pointer leading-none text-blue-500 hover:text-blue-800"
                  >
                    ×
                  </span>
                </span>
              );
            })}
          </div>
        )}
        <span className="shrink-0 text-[10px] text-gray-400">▾</span>
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded shadow-lg py-1">
          <button
            type="button"
            onClick={() => onChange([])}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
          >
            <input
              type="checkbox"
              readOnly
              checked={allSelected}
              className="pointer-events-none accent-blue-600"
            />
            <span className="font-semibold">All doctors</span>
          </button>
          <div className="border-t border-gray-100 my-1" />
          {doctors.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => toggle(d.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
            >
              <input
                type="checkbox"
                readOnly
                checked={selected.includes(d.id)}
                className="pointer-events-none accent-blue-600"
              />
              <span className="truncate">{d.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// The booking-export response shape can vary now that it's multi-doctor:
//   result: [ {row}, ... ]                                  → flat rows
//   result: [ { doctor_mapping_id, bookings:[...] }, ... ]  → grouped array
//   result: { "<mapping_id>": [ {row}, ... ], ... }         → keyed by doctor
//   result: { bookings: [ {row}, ... ] }                    → wrapped
// Flatten any of them into a flat row list, carrying doctor_mapping_id down.
const normalizeExportRows = (data: any): BookingExportRow[] => {
  const result = data?.result ?? data;
  if (Array.isArray(result)) {
    if (result.length && Array.isArray(result[0]?.bookings)) {
      return result.flatMap((g: any) =>
        (g.bookings ?? []).map((r: any) => ({
          doctor_mapping_id: g.doctor_mapping_id ?? r.doctor_mapping_id,
          doctor_name: g.doctor_name ?? r.doctor_name,
          ...r,
        }))
      );
    }
    return result as BookingExportRow[];
  }
  if (result && typeof result === "object") {
    if (Array.isArray(result.bookings)) return result.bookings;
    return Object.entries(result).flatMap(([key, val]: [string, any]) =>
      Array.isArray(val)
        ? val.map((r: any) => ({
            doctor_mapping_id: r.doctor_mapping_id ?? key,
            ...r,
          }))
        : []
    );
  }
  return [];
};

const DownloadManager: React.FC = () => {
  const today = moment().format("YYYY-MM-DD");
  const monthAgo = moment().subtract(1, "month").format("YYYY-MM-DD");
  // Selectable window: 90 days back to 90 days ahead.
  const minDate = moment().subtract(90, "days").format("YYYY-MM-DD");
  const maxDate = moment().add(90, "days").format("YYYY-MM-DD");

  const { hospitalID } = useHospDocData();

  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [format, setFormat] = useState<Format>("xlsx");
  const [downloading, setDownloading] = useState(false);
  // Selected doctor mapping_ids — empty ⇒ all doctors.
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<string[]>([]);

  // Real active doctors for the hospital → Doctor filter options.
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);

  useEffect(() => {
    if (!hospitalID) return;
    let active = true;
    (async () => {
      const res = await getDoctorListByHospitalId(hospitalID);
      if (!active || res?.status !== 200) return;
      const list = Array.isArray(res.data?.result) ? res.data.result : [];
      const opts: DoctorOption[] = list
        .filter((d: any) => d?.is_active !== false && d?.mapping_id)
        .map((d: any) => ({
          id: String(d.mapping_id),
          label: `Dr. ${d.full_name ?? d.doctor_name ?? "—"}`,
        }));
      setDoctors(opts);
    })();
    return () => {
      active = false;
    };
  }, [hospitalID]);

  const errMsg = (err: any, fallback: string) =>
    err?.response?.data?.message ||
    err?.response?.data?.error?.[0]?.msg ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  const generate = async () => {
    if (downloading) return;
    if (!startDate || !endDate) {
      toast.info("Pick a start and end date");
      return;
    }
    if (moment(endDate).isBefore(startDate)) {
      toast.error("End date can't be before start date");
      return;
    }

    // Empty selection ⇒ every loaded doctor.
    const targets =
      selectedDoctorIds.length === 0
        ? doctors
        : doctors.filter((d) => selectedDoctorIds.includes(d.id));

    if (targets.length === 0) {
      toast.info("No doctors available to export");
      return;
    }

    setDownloading(true);
    try {
      // ONE call with the full list of mapping ids; rows come back labelled by
      // doctor_mapping_id so we can show the Doctor column.
      const res = await exportBookings({
        doctor_mapping_ids: targets.map((d) => d.id),
        start_date: startDate,
        end_date: endDate,
      });

      const labelById = new Map(doctors.map((d) => [d.id, d.label]));
      const result = normalizeExportRows(res?.data);
      const summary = res?.data?.summary;

      const rows = result.map((r) => ({
        Doctor:
          (r.doctor_mapping_id && labelById.get(r.doctor_mapping_id)) ||
          r.doctor_name ||
          "—",
        "Patient name": r.patient_name ?? "",
        Phone: r.patient_number ?? "",
        "Booked date": r.booked_date
          ? moment(r.booked_date).format("DD MMM YYYY")
          : "",
        "Booking time": r.booking_time
          ? moment(r.booking_time).format("DD MMM YYYY, hh:mm A")
          : "",
        "Visit type": r.visit_type ?? "",
        "No-show": r.is_noshow ? "Yes" : "",
      }));

      if (rows.length === 0) {
        toast.info("No bookings found for this range.");
        return;
      }

      const COLS = [
        "Doctor",
        "Patient name",
        "Phone",
        "Booked date",
        "Booking time",
        "Visit type",
        "No-show",
      ];

      // Summary block on top of the sheet (grand total + per-doctor), then the
      // bookings table below it.
      const head: (string | number)[][] = [];
      if (summary) {
        head.push([
          `Booking export — ${moment(startDate).format(
            "DD MMM YYYY"
          )} to ${moment(endDate).format("DD MMM YYYY")}`,
        ]);
        head.push([
          "Total",
          summary.total ?? "",
          "New",
          summary.new ?? "",
          "Follow-up",
          summary.followup ?? "",
          "No-show",
          summary.noshow ?? "",
        ]);
        if (Array.isArray(summary.by_doctor) && summary.by_doctor.length) {
          head.push([]);
          head.push(["By doctor", "Total", "New", "Follow-up", "No-show"]);
          summary.by_doctor.forEach((d: any) =>
            head.push([
              labelById.get(d.doctor_mapping_id) || d.doctor_name || "—",
              d.total ?? "",
              d.new ?? "",
              d.followup ?? "",
              d.noshow ?? "",
            ])
          );
        }
        head.push([]); // spacer before the table
      }

      let ws;
      if (head.length) {
        ws = XLSX.utils.aoa_to_sheet(head);
        XLSX.utils.sheet_add_json(ws, rows, { header: COLS, origin: -1 });
      } else {
        ws = XLSX.utils.json_to_sheet(rows, { header: COLS });
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Bookings");

      const scope =
        targets.length === doctors.length
          ? "all-doctors"
          : `${targets.length}-doctors`;
      const filename = `bookings_${scope}_${startDate}_to_${endDate}.${format}`;

      // Explicit blob download — reliable for both csv and xlsx.
      const wbout = XLSX.write(wb, {
        bookType: format === "csv" ? "csv" : "xlsx",
        type: "array",
      });
      const blob = new Blob([wbout], {
        type:
          format === "csv"
            ? "text/csv;charset=utf-8"
            : "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${rows.length} bookings.`);
    } catch (err: any) {
      toast.error(errMsg(err, "Export failed"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-w-0 h-screen overflow-hidden bg-gray-50">
      <Header header value={"Download Manager"} />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-6">
        {/* Request an export */}
        <div className="bg-white border border-gray-300 rounded-lg p-5 max-w-3xl">
          <p className="text-base font-semibold text-gray-800">
            Download bookings
          </p>
          <p className="text-sm text-gray-500 mt-0.5">
            Export bookings for a date range (up to 90 days back or ahead) as a
            spreadsheet.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <div className="flex flex-col sm:col-span-2 lg:col-span-1">
              <label className="text-xs font-semibold text-gray-500 mb-1">
                Doctor
              </label>
              <DoctorMultiSelect
                doctors={doctors}
                selected={selectedDoctorIds}
                onChange={setSelectedDoctorIds}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1">
                From
              </label>
              <input
                type="date"
                value={startDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1">
                To
              </label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={maxDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1">
                Format
              </label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value as Format)}
                className="px-3 py-2 text-sm border border-gray-300 rounded bg-white"
              >
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
              </select>
            </div>
          </div>

          <button
            onClick={generate}
            disabled={downloading}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded bg-nextPatient text-white hover:bg-[#0d32a1] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <MdDownload className="text-base" />
            {downloading ? "Preparing…" : "Generate download"}
          </button>
        </div>

        {/* Bookings TAT — moved here from the Analytics page. */}
        <div className="mt-6 max-w-3xl">
          <BookingsExportCard />
        </div>
      </div>
    </div>
  );
};

export default DownloadManager;
